import type { AfterViewInit, OnDestroy } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { RepetitiveSubscription } from '@freelancer/decorators';
import { TimeUtils } from '@freelancer/time-utils';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { CameraComponent } from '@freelancer/ui/camera';
import { HeadingType } from '@freelancer/ui/heading';
import { PictureDisplay, PictureObjectFit } from '@freelancer/ui/picture';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import type { Observable } from 'rxjs';
import { Subscription } from 'rxjs';
import { last, map, take } from 'rxjs/operators';
import { CameraImageType } from '../camera-input';
import { ModalRef } from '../modal/modal-ref';

@Component({
  template: `
    <ng-container *flModalTitle>
      {{ modalTitle }}
    </ng-container>
    <div flTrackingSection="AppWebcam">
      <fl-heading
        [flMarginBottom]="'small'"
        [headingType]="HeadingType.H4"
        [size]="TextSize.MID"
      >
        <ng-container
          *ngIf="!this.modalHeader"
          i18n="Use your camera modal title"
        >
          Use your camera to take photo
        </ng-container>
        <ng-container *ngIf="this.modalHeader">
          {{ modalHeader }}
        </ng-container>
      </fl-heading>
      <div>
        <ng-container [ngTemplateOutlet]="content"></ng-container>
      </div>
      <div
        *ngIf="showCamera"
        class="CameraView"
        [flMarginBottom]="'small'"
      >
        <fl-camera
          [videoDevices]="videoDevices"
          [videoConstraints]="videoConstraints"
          [imageType]="imageType"
        ></fl-camera>
        <p
          class="CameraView-timer text-marketing_xmega font-light"
          *ngIf="showCameraTimer"
          i18n="Timer countdown when taking photo"
        >
          {{ cameraTimerValue$ | flAsync }}
        </p>
        <fl-button
          class="CameraView-switcher"
          *ngIf="showCameraSwitcher"
          flTrackingLabel="CameraSwitch"
          [color]="ButtonColor.DEFAULT"
          [disabled]="showCameraTimer"
          [size]="ButtonSize.SMALL"
          (click)="cycleDevice()"
        >
          <fl-icon [name]="'ui-camera-switch'"></fl-icon>
        </fl-button>
      </div>
      <fl-picture
        *ngIf="!showCamera && imagePreviewSrc"
        class="CameraView-picture"
        [alignCenter]="true"
        [display]="PictureDisplay.BLOCK"
        [flMarginBottom]="'small'"
        [fullWidth]="true"
        [objectFit]="PictureObjectFit.CONTAIN"
        [src]="imagePreviewSrc"
      ></fl-picture>
      <div class="CameraView-footer">
        <fl-button
          class="CameraView-action"
          *ngIf="showCamera"
          i18n="Take photo button"
          flTrackingLabel="TakeAPhoto"
          [color]="ButtonColor.SECONDARY"
          [disabled]="showCameraTimer || isSwitchingCamera"
          [size]="ButtonSize.LARGE"
          (click)="takePhoto()"
        >
          Take Photo
        </fl-button>
        <fl-button
          class="CameraView-action"
          *ngIf="!showCamera"
          i18n="Retake button"
          flTrackingLabel="Retake"
          [color]="ButtonColor.DEFAULT"
          [flMarginRight]="'small'"
          [size]="ButtonSize.LARGE"
          (click)="retakePhoto()"
        >
          Retake
        </fl-button>
        <fl-button
          class="CameraView-action"
          *ngIf="!showCamera"
          flTrackingLabel="Upload"
          [color]="ButtonColor.SECONDARY"
          [flMarginBottom]="'small'"
          [flMarginBottomTablet]="'none'"
          [size]="ButtonSize.LARGE"
          (click)="confirm()"
        >
          <ng-container
            *ngIf="!this.uploadCTA"
            i18n="Upload button"
          >
            Upload
          </ng-container>
          <ng-container *ngIf="this.uploadCTA">
            {{ uploadCTA }}
          </ng-container>
        </fl-button>
      </div>
    </div>
  `,
  styleUrls: ['./camera-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CameraModalComponent implements AfterViewInit, OnDestroy {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FontColor = FontColor;
  TextSize = TextSize;
  FontWeight = FontWeight;
  HeadingType = HeadingType;
  PictureDisplay = PictureDisplay;
  PictureObjectFit = PictureObjectFit;

  cameraTimerValue$: Observable<number>;
  imagePreviewSrc: string | undefined = undefined;
  remainingCaptureTime = 0;
  showCamera = true;
  showCameraSwitcher = false;
  showCameraTimer = false;
  isSwitchingCamera = false;

  @RepetitiveSubscription()
  private cameraTimerSubscription?: Subscription;

  @Input() captureTimeout = 5; // in seconds
  @Input() content: TemplateRef<HTMLElement>;
  @Input() modalHeader?: string;
  @Input() modalTitle?: string;
  @Input() uploadCTA?: string;
  @Input() videoDevices: readonly MediaDeviceInfo[];
  @Input() videoConstraints: MediaTrackConstraints;
  @Input() imageType: CameraImageType = CameraImageType.PNG;

  @ViewChild(CameraComponent) camera: CameraComponent;

  constructor(
    private cd: ChangeDetectorRef,
    private modalRef: ModalRef<CameraModalComponent>,
    private timeUtils: TimeUtils,
  ) {}

  ngAfterViewInit(): void {
    this.cycleDevice();
  }

  ngOnDestroy(): void {
    if (this.cameraTimerSubscription) {
      this.cameraTimerSubscription.unsubscribe();
    }
  }

  cancel(): void {
    this.modalRef.close();
  }

  confirm(): void {
    this.modalRef.close(this.imagePreviewSrc);
  }

  cycleDevice(): void {
    this.isSwitchingCamera = true;

    this.camera.cycleDevice().then(() => {
      this.setShowCameraSwitcher(true);
      this.isSwitchingCamera = false;
      this.cd.markForCheck();
    });
  }

  retakePhoto(): void {
    this.setShowCameraSwitcher(true);
    this.setShowCamera(true);

    // Show camera component first before accessing cycleDevice()
    this.timeUtils.setTimeout(() => {
      this.cycleDevice();
    });
  }

  setShowCamera(value: boolean): void {
    this.showCamera = value;
  }

  setShowCameraSwitcher(value: boolean): void {
    this.showCameraSwitcher = value && this.videoDevices.length > 1;
  }

  setShowCameraTimer(value: boolean): void {
    this.showCameraTimer = value;
  }

  takePhoto(): void {
    if (!this.showCameraTimer) {
      this.setShowCameraTimer(true);
      this.setShowCameraSwitcher(false);
      this.cameraTimerValue$ = this.timeUtils.rxTimer(0, 1000).pipe(
        take(this.captureTimeout + 1),
        map(val => this.captureTimeout - val),
      );

      this.cameraTimerSubscription = this.cameraTimerValue$
        .pipe(last(val => val >= this.captureTimeout))
        .subscribe(val => {
          this.imagePreviewSrc = this.camera.getSnapshot();
          this.setShowCameraTimer(false);
          this.setShowCamera(false);
        });
    }
  }
}
